
import { EventFilters } from "@/components/EventFilters";
import { RestaurantHeader } from "@/components/RestaurantHeader";
import { RestaurantList } from "@/components/RestaurantList";
import { RestaurantsMapbox } from "@/components/RestaurantsMapbox";
import { ViewToggle } from "@/components/ViewToggle";
import { FeaturedRestaurants } from "@/components/FeaturedRestaurants";
import { useState } from "react";
import { useRestaurantFilters } from "@/hooks/useRestaurantFilters";
import { Separator } from "@/components/ui/separator";
import { Helmet } from "react-helmet";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { RestaurantListContent } from "@/components/restaurant-list/RestaurantListContent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RestaurantSearch } from "@/components/RestaurantSearch";
import { useIsMobile } from "@/hooks/use-mobile";
import { RestaurantSubmissionDialog } from "@/components/RestaurantSubmissionDialog";

const Index = () => {
  console.log('Index - Component mounted');
  
  const [view, setView] = useState<"list" | "map">("list");
  const [currentTab, setCurrentTab] = useState<"all" | "favorites">("all");
  const [filters, setFilters] = useState({
    neighborhood: '',
    eventType: '',
    groupSize: '',
    priceRange: '',
    search: ''
  });

  const { session } = useAuth();
  const { favorites, isLoading: isFavoritesLoading } = useFavorites();
  const { data: restaurants = [], isLoading } = useRestaurantFilters(filters);
  const isMobile = useIsMobile();

  const favoritedRestaurants = restaurants.filter(restaurant => 
    favorites?.includes(restaurant.id)
  );

  const handleFilterChange = (type: string, value: string) => {
    console.log('Index - Filter changed:', { type, value });
    setFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSearch = (query: string) => {
    console.log('Index - Search query:', query);
    setFilters(prev => ({
      ...prev,
      search: query
    }));
  };

  const handleClearFilters = () => {
    console.log('Index - Clearing all filters');
    setFilters({
      neighborhood: '',
      eventType: '',
      groupSize: '',
      priceRange: '',
      search: ''
    });
  };

  return (
    <div className="min-h-screen bg-accent flex flex-col">
      <Helmet>
        <title>NYC Private Event Spaces | Find Restaurant Venues for Events</title>
        <meta name="description" content="Discover the perfect NYC restaurant venue for your next private event. Browse private dining rooms, event spaces, and party venues for corporate events, celebrations, and more." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="NYC Private Event Spaces | Find Restaurant Venues for Events" />
        <meta property="og:description" content="Discover the perfect NYC restaurant venue for your next private event. Browse private dining rooms, event spaces, and party venues for corporate events, celebrations, and more." />
        <meta property="og:image" content="/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NYC Private Event Spaces | Find Restaurant Venues for Events" />
        <meta name="twitter:description" content="Discover the perfect NYC restaurant venue for your next private event. Browse private dining rooms, event spaces, and party venues for corporate events, celebrations, and more." />
        <meta name="twitter:image" content="/og-image.png" />
        
        {/* Additional SEO tags */}
        <meta name="keywords" content="NYC private dining, restaurant event spaces, private party venues, corporate event venues, private dining rooms NYC, restaurant venues, private events" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://onthetown.nyc" />
      </Helmet>

      <RestaurantHeader onSearch={handleSearch} />
      
      <main className="px-4 lg:px-8 flex-1 flex flex-col">
        <div className="pt-6 md:pt-8 -mt-8 bg-gradient-to-b from-primary/5 to-transparent rounded-b-3xl">
          <FeaturedRestaurants />
        </div>
        
        <Separator className="my-6" />
        
        <div className={`space-y-4 flex-1 flex flex-col ${view === "map" ? "overflow-hidden" : ""}`}>
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-6 min-w-0">
                <h2 className="text-xl font-semibold text-primary whitespace-nowrap">Find Your Perfect Venue</h2>
                {!isMobile && (
                  <div className="w-64 min-w-0">
                    <RestaurantSearch onSearch={handleSearch} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <RestaurantSubmissionDialog />
                <ViewToggle view={view} onViewChange={setView} />
              </div>
            </div>
            
            <EventFilters 
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </div>
          
          <div className={`relative flex-1 min-h-0 ${view === "map" ? "flex" : ""}`}>
            {session ? (
              <Tabs defaultValue="all" className="w-full" onValueChange={(value) => setCurrentTab(value as "all" | "favorites")}>
                <TabsList>
                  <TabsTrigger value="all">All Restaurants</TabsTrigger>
                  <TabsTrigger value="favorites">
                    My Favorites ({favoritedRestaurants.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="all" className="mt-4">
                  <div className={`transition-opacity duration-300 ${view === "list" ? 'opacity-100' : 'opacity-0'}`}>
                    <RestaurantList filters={filters} />
                  </div>
                </TabsContent>
                <TabsContent value="favorites" className="mt-4">
                  <div className={`transition-opacity duration-300 ${view === "list" ? 'opacity-100' : 'opacity-0'}`}>
                    {isFavoritesLoading ? (
                      <div className="text-center py-8">Loading favorites...</div>
                    ) : favoritedRestaurants.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>You haven't favorited any restaurants yet.</p>
                        <p className="mt-2">Click the heart icon on any restaurant to add it to your favorites!</p>
                      </div>
                    ) : (
                      <RestaurantListContent restaurants={favoritedRestaurants} />
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className={`transition-opacity duration-300 ${view === "list" ? 'opacity-100' : 'opacity-0'}`}>
                <RestaurantList filters={filters} />
              </div>
            )}
            
            <div className={`${view === "map" ? "absolute inset-0" : "sticky bottom-4"}`}>
              <RestaurantsMapbox 
                restaurants={session && currentTab === "favorites" ? favoritedRestaurants : restaurants} 
                isVisible={view === "map"} 
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Index;
